import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../lib/auth.jsx'
import { TaskboardContext } from '../lib/taskboardContext.js'
import { useHashRoute, mergeQuery, indexQuery, useSearchInput, peekToken, withPeek, clearPeek } from '../lib/hashRoute.js'
import { matchRoute, isActive, isFullBleed } from '../lib/routes.js'
import * as navPersist from '../lib/navPersist.js'
import {
  watchUsers,
  watchLabels,
  watchMyProjects,
  watchProjectTasks,
  watchMyTasks,
  watchMyNotifications,
  watchTools,
  watchTeamDates,
  watchPage,
  watchStatuses,
  savePage as dbSavePage,
  createNotification,
  markNotificationRead as dbMarkNotificationRead,
  markAllNotificationsRead as dbMarkAllNotificationsRead,
  createTask as dbCreateTask,
  updateTask as dbUpdateTask,
  setTaskStatus as dbSetTaskStatus,
  setAssignee as dbSetAssignee,
  toggleLabel as dbToggleLabel,
  addComment as dbAddComment,
  createProject as dbCreateProject,
  inviteUser as dbInviteUser,
  setProjectMembers as dbSetProjectMembers,
  deleteTask as dbDeleteTask,
  updateProject as dbUpdateProject,
  deleteProject as dbDeleteProject,
  createTool as dbCreateTool,
  updateTool as dbUpdateTool,
  deleteTool as dbDeleteTool,
  createTeamDate as dbCreateTeamDate,
  updateTeamDate as dbUpdateTeamDate,
  deleteTeamDate as dbDeleteTeamDate,
  updateUserProfile as dbUpdateUserProfile,
  createStatus as dbCreateStatus,
  updateStatus as dbUpdateStatus,
  deleteStatus as dbDeleteStatus,
  seedDefaultStatuses as dbSeedStatuses,
  createLabel as dbCreateLabel,
  updateLabel as dbUpdateLabel,
  deleteLabel as dbDeleteLabel,
  watchEngagements,
  watchAllEngagements,
  watchEngagementTasks,
  createEngagement as dbCreateEngagement,
  updateEngagement as dbUpdateEngagement,
  moveEngagementToStage as dbMoveEngagementToStage,
  deleteEngagement as dbDeleteEngagement,
  createEngagementStage as dbCreateEngagementStage,
  updateEngagementStage as dbUpdateEngagementStage,
  deleteEngagementStage as dbDeleteEngagementStage,
  seedDefaultEngagementStages as dbSeedEngagementStages,
  watchContacts, watchCompanies, watchContact, watchCompany,
  watchPropertyDefs, watchAssociations, watchActivities,
  createContact, createCompany, updateContact, updateCompany,
  associate, unassociate, logActivity, seedCorePropertyDefs,
  watchAllTasks,
  watchAllEngagementStages,
  watchEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate,
  watchMySavedViews,
  createSavedView,
  deleteSavedView,
  watchGoogleSettings,
  deleteContact, deleteCompany,
} from '../lib/data.js'
import { syncEngagementEvent, syncTeamDateEvent, removeCalendarEvent } from '../lib/calendarSync.js'
import { orderBetween, orderForIndex, DEFAULT_STATUSES, ENGAGEMENT_BOARD_PROJECT, objToken, parseToken, dispatchToken, countBy, filterEngagements, pickEngagementBoard, countCompanyDependents, engagementType } from '../lib/format.js'
import Sidebar from './Sidebar.jsx'
import { AppShell, Hamburger, Fab, TabBar, useViewFade } from '../ds/index.js'
import Toolbar from './Toolbar.jsx'
import NotificationsBell from './NotificationsBell.jsx'
import ListView from '../views/ListView.jsx'
import BoardView from '../views/BoardView.jsx'
import CalendarView from '../views/CalendarView.jsx'
import TableView from '../views/TableView.jsx'
import MyTasksView from '../views/MyTasksView.jsx'
import TeamView from '../views/TeamView.jsx'
import ToolsView from '../views/ToolsView.jsx'
import PlaybookView from '../views/PlaybookView.jsx'
import EngagementsView from '../views/EngagementsView.jsx'
import EngagementListView from '../views/EngagementListView.jsx'
import EngagementTableView from '../views/EngagementTableView.jsx'
import EngagementCalendarView from '../views/EngagementCalendarView.jsx'
import EngagementRecord from './EngagementRecord.jsx'
import EngagementToolbar from './EngagementToolbar.jsx'
import TaskDetail from './TaskDetail.jsx'
import NewTaskModal from './NewTaskModal.jsx'
import NewProjectModal from './NewProjectModal.jsx'
import ProjectMembers from './ProjectMembers.jsx'
import ProjectMenu from './ProjectMenu.jsx'
import SettingsPage from './SettingsPage.jsx'
import ContactsView from '../views/ContactsView.jsx'
import CompaniesView from '../views/CompaniesView.jsx'
import ContactRecord from './ContactRecord.jsx'
import CompanyRecord from './CompanyRecord.jsx'
import CommandPalette from './CommandPalette.jsx'
import SubmissionPeek from '../views/forms/SubmissionPeek.jsx'
import { watchSubmission, deleteSubmission, callPromote } from '../lib/formsData.js'
import { promoteErrorMessage } from '../lib/formsView.js'
import RecordPeek from './RecordPeek.jsx'
import HomeView from '../views/HomeView.jsx'
import FormsIndex from '../views/forms/FormsIndex.jsx'
import FormPage from '../views/forms/FormPage.jsx'
import { MagnifyingGlass, House, ListChecks, UsersThree, List, Plus } from '@phosphor-icons/react'

// Shared association side-effects. `extraWrites` is a list of thunks (() => Promise)
// run in the same batch as the activity writes — AFTER associate/unassociate — so a
// failed association skips them and any rejection is caught, matching pre-refactor behavior.
async function linkRecords({ selfToken, selfLabel, otherToken, otherLabel, uid, extraWrites = [] }) {
  await associate(selfToken, otherToken, null, uid)
  await Promise.all([
    logActivity(selfToken, { type: 'association', body: `Linked ${otherLabel}` }, uid),
    logActivity(otherToken, { type: 'association', body: `Linked ${selfLabel}` }, uid),
    ...extraWrites.map((w) => w()),
  ]).catch((err) => console.error('link side-effects failed', err))
}
async function unlinkRecords({ selfToken, selfLabel, otherToken, otherLabel, uid, extraWrites = [] }) {
  await unassociate(selfToken, otherToken)
  await Promise.all([
    logActivity(selfToken, { type: 'association', body: `Unlinked ${otherLabel}` }, uid),
    logActivity(otherToken, { type: 'association', body: `Unlinked ${selfLabel}` }, uid),
    ...extraWrites.map((w) => w()),
  ]).catch((err) => console.error('unlink side-effects failed', err))
}

export default function Taskboard() {
  const { user, updateProfile, signOut } = useAuth()

  // ── data ──
  const [users, setUsers] = useState([])
  const [labels, setLabels] = useState([])
  const [projects, setProjects] = useState([])
  const [tasks, setTasks] = useState([])
  const [myTasks, setMyTasks] = useState([])
  const [statuses, setStatuses] = useState([])
  const [statusesLoaded, setStatusesLoaded] = useState(false)
  const seededRef = useRef(false)
  const [notifications, setNotifications] = useState([])
  const [tools, setTools] = useState([])
  const [teamDates, setTeamDates] = useState([])
  const [playbook, setPlaybook] = useState(null)
  const [engagements, setEngagements] = useState([])
  const [engStages, setEngStages] = useState([])
  const [engStagesLoaded, setEngStagesLoaded] = useState(false)
  const engSeededRef = useRef(false)
  const [engagementTasks, setEngagementTasks] = useState([])
  const [engAssocs, setEngAssocs] = useState([])
  const [engActs, setEngActs] = useState([])
  const [contacts, setContacts] = useState([])
  const [companies, setCompanies] = useState([])
  const [contactDefs, setContactDefs] = useState([])
  const [companyDefs, setCompanyDefs] = useState([])
  const crmSeededRef = useRef(false)
  const [engagementDefs, setEngagementDefs] = useState([])
  const [emailTemplates, setEmailTemplates] = useState([])

  // ── home dashboard (unfiltered, route-scoped) ──
  const [allEngagements, setAllEngagements] = useState([])
  const [homeEngStages, setHomeEngStages] = useState([])
  const [allTasks, setAllTasks] = useState([])

  // ── nav feed (always-on, all boards) — drives Sidebar's Speaking/PR accordions ──
  const [navEngagements, setNavEngagements] = useState([])

  // ── ui state ──
  const { route, navigate } = useHashRoute()

  const q = route.query || {}
  const inEnum = (v, allowed, dflt) => (allowed.includes(v) ? v : dflt)
  const view = route.type === 'project' ? inEnum(q.view, ['list', 'board', 'calendar', 'table'], 'list') : 'list'
  const engView = route.type === 'engagements' ? inEnum(q.view, ['board', 'list', 'calendar', 'table'], 'board') : 'board'
  const labelFilter = q.label || null
  const assigneeFilter = q.assignee || null
  const phaseFilter = q.phase || null
  const incompleteOnly = q.incomplete === '1'
  const dueSoonOnly = q.dueSoon === '1'
  const search = q.q || ''
  const engOwnerFilter = q.owner || null
  const engSearch = q.q || ''
  const engStageFilter = q.stage || null
  const selectedId = route.type === 'project' ? (q.task || null) : null

  // Every config setter writes the URL. Defaults are passed as null so mergeQuery omits them.
  const setQuery = useCallback((patch, opts) => navigate(mergeQuery(route, patch), opts), [route, navigate])
  const setView = useCallback((v) => {
    setQuery({ view: v === 'list' ? null : v })
    // Switching to list is explicit intent but drops the ?view= param, so the
    // persist effect (which only records non-default views) can't see it —
    // record it here so a deliberate switch to list still updates the saved view.
    if (v === 'list' && route.type === 'project' && route.projectId) navPersist.setView(route.projectId, 'list')
  }, [setQuery, route])
  const setEngView = useCallback((v) => setQuery({ view: v === 'board' ? null : v }), [setQuery])
  const setLabelFilter = useCallback((v) => setQuery({ label: v }), [setQuery])
  const setAssigneeFilter = useCallback((v) => setQuery({ assignee: v }), [setQuery])
  const setPhaseFilter = useCallback((v) => setQuery({ phase: v }), [setQuery])
  const setIncompleteOnly = useCallback((v) => setQuery({ incomplete: v ? '1' : null }), [setQuery])
  const setDueSoonOnly = useCallback((v) => setQuery({ dueSoon: v ? '1' : null }), [setQuery])
  const setEngOwnerFilter = useCallback((v) => setQuery({ owner: v }), [setQuery])
  const setEngStageFilter = useCallback((v) => setQuery({ stage: v }), [setQuery])
  const setSearchQuery = useCallback((v) => navigate(mergeQuery(route, { q: v }), { replace: true }), [route, navigate])

  const [sidebarOpen, setSidebarOpen] = useState(false) // mobile drawer
  const viewFadeRef = useRef(null)
  useViewFade(viewFadeRef, view)
  const engViewFadeRef = useRef(null)
  useViewFade(engViewFadeRef, engView)
  // List view is an accordion: exactly one status section open at a time.
  // null = nothing chosen yet (ListView opens the first section); '' = all closed.
  const [openSection, setOpenSection] = useState(null)
  const [currentProjectId, setCurrentProjectId] = useState(null)
  const [savedViews, setSavedViews] = useState([])
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('tm-theme') || 'dark'
    } catch {
      return 'dark'
    }
  })
  const [showNewTask, setShowNewTask] = useState(false)
  const [showNewProject, setShowNewProject] = useState(false)
  const [projectsLoaded, setProjectsLoaded] = useState(false)
  const [cmdkOpen, setCmdkOpen] = useState(false)

  // Global ⌘K / Ctrl-K toggles the command palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setCmdkOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // ── subscriptions ──
  useEffect(() => watchUsers(setUsers), [])
  useEffect(() => watchLabels(setLabels), [])
  useEffect(
    () =>
      watchMyProjects(user.uid, (ps) => {
        setProjects(ps)
        setProjectsLoaded(true)
      }),
    [user.uid],
  )
  useEffect(() => watchMyTasks(user.uid, setMyTasks), [user.uid])
  useEffect(() => watchMySavedViews(user.uid, setSavedViews), [user.uid])
  useEffect(() => watchMyNotifications(user.uid, setNotifications), [user.uid])
  useEffect(() => watchStatuses((s) => { setStatuses(s); setStatusesLoaded(true) }), [])
  useEffect(() => watchTools(setTools), [])
  useEffect(() => watchTeamDates(setTeamDates), [])
  useEffect(() => watchPage('playbook', setPlaybook), [])
  useEffect(() => {
    if (!currentProjectId) return undefined
    return watchProjectTasks(currentProjectId, setTasks)
  }, [currentProjectId])
  const engBoard = route.type === 'engagements' ? route.board : null
  const engRecordId = route.type === 'engagements' ? route.recordId || null : null
  useEffect(() => {
    if (!engBoard) { setEngagements([]); return undefined }
    return watchEngagements(engBoard, setEngagements)
  }, [engBoard])
  // One subscription across every type. Per-board subscriptions merged by
  // `s.board` duplicated typed stages (they carry `type`, not `board`) on every
  // snapshot and never loaded advisory; boardStages below filters by type.
  useEffect(() => watchAllEngagementStages((a) => {
    setEngStages(a)
    setEngStagesLoaded(true)
  }), [])
  useEffect(() => {
    if (!engRecordId) { setEngagementTasks([]); return undefined }
    return watchEngagementTasks(engRecordId, setEngagementTasks)
  }, [engRecordId])
  useEffect(() => {
    if (!engRecordId) { setEngAssocs([]); setEngActs([]); return undefined }
    const token = objToken('engagement', engRecordId)
    const u1 = watchAssociations(token, setEngAssocs)
    const u2 = watchActivities(token, setEngActs)
    return () => { u1(); u2() }
  }, [engRecordId])
  useEffect(() => (user ? watchContacts(setContacts) : undefined), [user])
  useEffect(() => (user ? watchCompanies(setCompanies) : undefined), [user])
  useEffect(() => (user ? watchPropertyDefs('contact', setContactDefs) : undefined), [user])
  useEffect(() => (user ? watchPropertyDefs('company', setCompanyDefs) : undefined), [user])
  useEffect(() => {
    if (!user || crmSeededRef.current) return
    crmSeededRef.current = true
    seedCorePropertyDefs(user.uid)
  }, [user])

  useEffect(() => (user ? watchPropertyDefs('engagement', setEngagementDefs) : undefined), [user])
  useEffect(() => (user ? watchEmailTemplates(setEmailTemplates) : undefined), [user])

  // ── Home dashboard subscriptions — unfiltered engagements + every engagement
  // stage, only while on the Home route (avoid always-on read cost elsewhere).
  // Wave 2: the `deals` mirror these used to read is gone; the pipeline forecast
  // now runs on the engagements themselves. ──
  useEffect(() => {
    if (route.type !== 'home') return undefined
    const u1 = watchAllEngagements(setAllEngagements)
    const u2 = watchAllEngagementStages(setHomeEngStages)
    const u3 = watchAllTasks(setAllTasks)
    return () => { u1(); u2(); u3() }
  }, [route.type])

  useEffect(() => (user ? watchAllEngagements(setNavEngagements) : undefined), [user])

  // ── Google Calendar write-through settings ──
  const [googleSettings, setGoogleSettings] = useState(null)
  useEffect(() => watchGoogleSettings(setGoogleSettings), [])
  const googleCalendarId = googleSettings?.calendarId || null

  const advisoryEngagements = useMemo(() => navEngagements.filter((e) => engagementType(e) === 'advisory'), [navEngagements])
  const speakingEngagements = useMemo(() => navEngagements.filter((e) => engagementType(e) === 'speaking'), [navEngagements])
  const prEngagements = useMemo(() => navEngagements.filter((e) => engagementType(e) === 'pr'), [navEngagements])

  // Active (non-archived) projects drive the tabs; archived ones live in Settings.
  const activeProjects = useMemo(() => projects.filter((p) => !p.archived), [projects])
  const archivedProjects = useMemo(() => projects.filter((p) => p.archived), [projects])

  // Default to the first active project; if the current one is archived/removed,
  // fall back to an active one.
  useEffect(() => {
    if (!activeProjects.length) return
    if (!currentProjectId || !activeProjects.find((p) => p.id === currentProjectId)) {
      setCurrentProjectId(activeProjects[0].id)
    }
  }, [activeProjects, currentProjectId])

  // Keep currentProjectId in step with a project route (covers hash/Back nav).
  useEffect(() => {
    if (route.type === 'project' && route.projectId) setCurrentProjectId(route.projectId)
  }, [route])

  // Remember where the user was (content routes only; settings is skipped inside).
  useEffect(() => { navPersist.saveLastRoute(route) }, [route])

  // Persist the chosen view for the current project — but only an explicit,
  // non-default view. A bare `#/project/<id>` deep-link has no ?view= and
  // derives 'list', which must NOT clobber a saved board/calendar preference.
  // An explicit switch to list is recorded in setView instead (see below).
  useEffect(() => {
    if (navPersist.shouldPersistProjectView(route, view) && currentProjectId) navPersist.setView(currentProjectId, view)
  }, [view, route, currentProjectId])

  // Resolve startup location on a fresh open (no explicit deep link), once projects have loaded.
  const didStartupRef = useRef(false)
  // Snapshot the persisted nav during first render (before any effect can run) so the
  // saveLastRoute effect below can't clobber it with the initial route first.
  const startupNavRef = useRef(navPersist.loadNav())
  useEffect(() => {
    if (didStartupRef.current || !user) return
    const h = window.location.hash
    if (h && h !== '#/') { didStartupRef.current = true; return } // explicit deep link — honor it
    if (!activeProjects.length) return // wait for projects so deleted-project check is valid
    didStartupRef.current = true
    const target = navPersist.resolveStartup(
      user.startupBehavior || 'continue',
      startupNavRef.current,
      (id) => activeProjects.some((p) => p.id === id),
    )
    if (target.type !== 'home') navigate(target)
  }, [user, activeProjects, navigate])

  // Seed default statuses into Firestore once, and only after the snapshot has resolved empty.
  useEffect(() => {
    if (!user || !statusesLoaded || statuses.length > 0 || seededRef.current) return
    seededRef.current = true
    dbSeedStatuses(user.uid)
  }, [user, statusesLoaded, statuses.length])

  useEffect(() => {
    if (!user || !engStagesLoaded || engStages.length > 0 || engSeededRef.current) return
    engSeededRef.current = true
    dbSeedEngagementStages(user.uid)
  }, [user, engStagesLoaded, engStages.length])

  // Theme application + persistence.
  useEffect(() => {
    document.documentElement.classList.toggle('light', theme === 'light')
    try {
      localStorage.setItem('tm-theme', theme)
    } catch {
      /* ignore */
    }
  }, [theme])

  // ── lookup maps ──
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.uid, u])), [users])
  const labelsById = useMemo(() => Object.fromEntries(labels.map((l) => [l.id, l])), [labels])
  const currentProject = useMemo(
    () => projects.find((p) => p.id === currentProjectId) || null,
    [projects, currentProjectId],
  )
  const liveStatuses = useMemo(() => (statuses.length ? statuses : DEFAULT_STATUSES), [statuses])
  const statusesById = useMemo(() => Object.fromEntries(liveStatuses.map((s) => [s.id, s])), [liveStatuses])
  const doneStatusId = useMemo(() => (liveStatuses.find((s) => s.done) || {}).id || 'done', [liveStatuses])
  const firstOpenStatusId = useMemo(() => (liveStatuses.find((s) => !s.done) || {}).id || 'todo', [liveStatuses])

  // ── actions (write to Firestore) ──
  const openTask = useCallback((id) => navigate(mergeQuery(route, { task: id })), [route, navigate])
  const closeTask = useCallback(() => navigate(mergeQuery(route, { task: null }), { replace: true }), [route, navigate])
  // Peek panel: open pushes history (Back closes it, link is shareable); close replaces.
  const openPeek = useCallback((token) => navigate(withPeek(route, token)), [route, navigate])
  const closePeek = useCallback(() => navigate(clearPeek(route), { replace: true }), [route, navigate])
  // Navigate to a project (sidebar / notifications / My Tasks click).
  const goToProject = useCallback((projectId) => {
    setCurrentProjectId(projectId)
    const saved = navPersist.getView(projectId)
    navigate({ type: 'project', projectId, query: saved && saved !== 'list' ? { view: saved } : undefined })
  }, [navigate])
  const goToHome = useCallback(() => navigate({ type: 'home' }), [navigate])
  const goToMyTasks = useCallback(() => navigate({ type: 'mytasks' }), [navigate])
  const goToTeam = useCallback(() => navigate({ type: 'team' }), [navigate])
  const goToTools = useCallback(() => navigate({ type: 'tools' }), [navigate])
  const goToPlaybook = useCallback(() => navigate({ type: 'playbook' }), [navigate])
  const goToSpeaking = useCallback(() => navigate({ type: 'engagements', board: 'speaking' }), [navigate])
  const goToPR = useCallback(() => navigate({ type: 'engagements', board: 'pr' }), [navigate])
  const goToAdvisory = useCallback(() => navigate({ type: 'engagements', board: 'advisory' }), [navigate])
  const goToContacts = useCallback(() => navigate({ type: 'crm', object: 'contacts' }), [navigate])
  const goToCompanies = useCallback(() => navigate({ type: 'crm', object: 'companies' }), [navigate])
  // Carry the index's filters/search/view onto the record URL so in-app Back can restore them (URL stays the source of truth).
  const openContact = useCallback((id) => navigate({ type: 'crm', object: 'contacts', recordId: id, query: indexQuery(route) }), [navigate, route])
  const openCompany = useCallback((id) => navigate({ type: 'crm', object: 'companies', recordId: id, query: indexQuery(route) }), [navigate, route])
  const createContactAction = useCallback(async () => {
    const id = await createContact({ firstName: 'New', lastName: 'Contact' }, user.uid)
    openContact(id)
  }, [user, openContact])
  const createCompanyAction = useCallback(async () => {
    const id = await createCompany({ name: 'New Company' }, user.uid)
    openCompany(id)
  }, [user, openCompany])
  const changeContactField = useCallback((id, key, value, isCore) => {
    updateContact(id, isCore ? { [key]: value } : { [`custom.${key}`]: value }, user.uid)
  }, [user])
  const changeCompanyField = useCallback((id, key, value, isCore) => {
    updateCompany(id, isCore ? { [key]: value } : { [`custom.${key}`]: value }, user.uid)
  }, [user])
  const goToForms = useCallback(() => navigate({ type: 'forms' }), [navigate])

  // ── canned email templates (register item 9): one shared, team-editable list ──
  const createTemplate = useCallback((t) => createEmailTemplate(t, user.uid), [user.uid])
  const updateTemplate = useCallback((id, patch) => updateEmailTemplate(id, patch, user.uid), [user.uid])
  const deleteTemplate = useCallback((id) => deleteEmailTemplate(id), [])
  // Open a task that may live in another project (notifications / My Tasks).
  const openTaskInProject = useCallback((projectId, taskId) => {
    if (projectId) {
      setCurrentProjectId(projectId)
      const saved = navPersist.getView(projectId)
      navigate({ type: 'project', projectId, query: { task: taskId, ...(saved && saved !== 'list' ? { view: saved } : {}) } })
    } else {
      navigate(mergeQuery(route, { task: taskId }))
    }
  }, [navigate, route])
  const markNotificationRead = useCallback((id) => dbMarkNotificationRead(id), [])
  const markAllNotificationsRead = useCallback(
    () => dbMarkAllNotificationsRead(notifications.filter((n) => !n.read).map((n) => n.id)),
    [notifications],
  )
  const archiveProject = useCallback((projectId, archived) => dbUpdateProject(projectId, { archived }), [])

  const toggleDone = useCallback(
    (task) => {
      const next = statusesById[task.status]?.done ? firstOpenStatusId : doneStatusId
      dbSetTaskStatus(task.id, next, user.uid, undefined, statusesById[next]?.name)
    },
    [user.uid, statusesById, firstOpenStatusId, doneStatusId],
  )

  const setStatus = useCallback((taskId, key) => dbSetTaskStatus(taskId, key, user.uid, undefined, statusesById[key]?.name), [user.uid, statusesById])

  const findTask = useCallback(
    (taskId) => tasks.find((t) => t.id === taskId) || myTasks.find((t) => t.id === taskId) || null,
    [tasks, myTasks],
  )

  const setAssignee = useCallback(
    (taskId, uid) => {
      dbSetAssignee(taskId, uid, user.uid, usersById[uid]?.name)
      if (uid && uid !== user.uid) {
        const t = findTask(taskId)
        createNotification({
          forUid: uid,
          actorUid: user.uid,
          type: 'assignment',
          taskId,
          projectId: t?.projectId || null,
          text: `${user.name} assigned you “${t?.title || 'a task'}”`,
        })
      }
    },
    [user.uid, user.name, usersById, findTask],
  )

  const setDue = useCallback((taskId, date) => dbUpdateTask(taskId, { dueDate: date }), [])

  const updateTaskFields = useCallback((taskId, patch) => dbUpdateTask(taskId, patch), [])

  const toggleLabelOnTask = useCallback((task, labelId) => {
    const has = (task.labelIds || []).includes(labelId)
    const next = has
      ? task.labelIds.filter((x) => x !== labelId)
      : [...(task.labelIds || []), labelId]
    dbToggleLabel(task.id, next)
  }, [])

  const addCommentToTask = useCallback(
    async (taskId, text) => {
      // Resolve @Mentions to uids by matching against known user names.
      const mentions = users
        .filter((u) => text.includes('@' + u.name))
        .map((u) => u.uid)
      await dbAddComment(taskId, { authorUid: user.uid, text, mentions })
      // Notify mentioned teammates (skip self).
      const t = findTask(taskId)
      mentions
        .filter((uid) => uid !== user.uid)
        .forEach((uid) =>
          createNotification({
            forUid: uid,
            actorUid: user.uid,
            type: 'mention',
            taskId,
            projectId: t?.projectId || null,
            text: `${user.name} mentioned you on “${t?.title || 'a task'}”`,
          }),
        )
    },
    [users, user.uid, user.name, findTask],
  )

  const createTask = useCallback(
    async (draft) => {
      const id = await dbCreateTask(draft, user.uid)
      const pid = draft.projectId || currentProjectId
      setShowNewTask(false)
      if (pid) {
        setCurrentProjectId(pid)
        const saved = navPersist.getView(pid)
        navigate({ type: 'project', projectId: pid, query: { task: id, ...(saved && saved !== 'list' ? { view: saved } : {}) } })
      }
      return id
    },
    [user.uid, currentProjectId, navigate],
  )

  // Inline / board quick-add — create without opening the slide-over.
  const quickAddTask = useCallback(
    (title, status) => {
      const trimmed = (title || '').trim()
      if (!trimmed || !currentProjectId) return undefined
      return dbCreateTask(
        {
          projectId: currentProjectId,
          title: trimmed,
          status: status || firstOpenStatusId,
          assigneeUid: user.uid,
          labelIds: [],
        },
        user.uid,
      )
    },
    [currentProjectId, user.uid, firstOpenStatusId],
  )

  const inviteTeammate = useCallback((name, email) => dbInviteUser({ name, email }, user.uid), [user.uid])
  const updateProjectMembers = useCallback((projectId, memberUids) => dbSetProjectMembers(projectId, memberUids), [])

  const deleteTaskById = useCallback(
    async (taskId) => {
      await dbDeleteTask(taskId)
      if (route.type === 'project' && route.query && route.query.task === taskId) {
        navigate(mergeQuery(route, { task: null }), { replace: true })
      }
    },
    [route, navigate],
  )
  const renameProject = useCallback((projectId, patch) => dbUpdateProject(projectId, patch), [])
  const deleteProjectById = useCallback(
    async (projectId) => {
      await dbDeleteProject(projectId)
      // Move to another project (or none) after deletion.
      setCurrentProjectId((cur) => {
        if (cur !== projectId) return cur
        const next = projects.find((p) => p.id !== projectId)
        return next ? next.id : null
      })
    },
    [projects],
  )

  const createProject = useCallback(
    async (draft) => {
      const id = await dbCreateProject(
        { name: draft.name, subtitle: draft.subtitle, memberUids: [user.uid] },
        user.uid,
      )
      setCurrentProjectId(id)
      navigate({ type: 'project', projectId: id })
      setShowNewProject(false)
      return id
    },
    [user.uid, navigate],
  )

  const createTool = useCallback((t) => dbCreateTool(t, user.uid), [user.uid])
  const updateTool = useCallback((id, patch) => dbUpdateTool(id, patch, user.uid), [user.uid])
  const deleteTool = useCallback((id) => dbDeleteTool(id), [])

  const saveMyProfile = useCallback((patch) => dbUpdateUserProfile(user.uid, patch), [user.uid])

  // Calendar write-through: always AFTER the Firestore save, never blocking it.
  // Failures just leave the row stale — the SyncChip is the retry surface.
  const fireCalendarSync = useCallback((kind, row) => {
    const run = kind === 'engagement' ? syncEngagementEvent : syncTeamDateEvent
    run(row, googleCalendarId, user.uid).catch((e) => console.warn('Calendar sync failed:', e.message))
  }, [googleCalendarId, user.uid])

  const addTeamDate = useCallback(async (d) => {
    const id = await dbCreateTeamDate(d, user.uid)
    fireCalendarSync('teamDate', { id, ...d })
    return id
  }, [user.uid, fireCalendarSync])
  const updateTeamDateAction = useCallback(async (id, patch) => {
    await dbUpdateTeamDate(id, patch, user.uid)
    const row = teamDates.find((x) => x.id === id)
    // Any edit bumps updatedAt — re-sync the projection (same posture as engagements).
    fireCalendarSync('teamDate', { ...(row || {}), id, ...patch })
  }, [user.uid, teamDates, fireCalendarSync])
  const deleteTeamDateAction = useCallback(async (d) => {
    removeCalendarEvent(d, googleCalendarId)
    await dbDeleteTeamDate(d.id)
  }, [googleCalendarId])
  const savePlaybook = useCallback((body) => dbSavePage('playbook', body, user.uid), [user.uid])
  const boardStages = useMemo(
    () => engStages.filter((s) => engBoard && s.type === engBoard).sort((a, b) => a.order - b.order),
    [engStages, engBoard],
  )
  const filteredEngagements = useMemo(() => {
    let r = filterEngagements(engagements, { ownerFilter: engOwnerFilter, search: engSearch })
    if (engStageFilter) r = r.filter((e) => e.stage === engStageFilter)
    return r
  }, [engagements, engOwnerFilter, engSearch, engStageFilter])
  const companiesById = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c])), [companies])
  const openEngagement = useCallback((id, board) => {
    const resolved = pickEngagementBoard(id, board, navEngagements, engBoard)
    navigate({ type: 'engagements', board: resolved, recordId: id, query: indexQuery(route) })
  }, [navEngagements, engBoard, navigate, route])
  const createEngagementAction = useCallback(async () => {
    const first = boardStages[0]
    const maxOrder = engagements.length ? Math.max(...engagements.map((e) => e.order)) : 0
    const id = await dbCreateEngagement({ type: engBoard, title: 'New engagement', stage: first ? first.id : '', order: maxOrder + 1000 }, user.uid)
    navigate({ type: 'engagements', board: engBoard, recordId: id })
    return id
  }, [boardStages, engagements, engBoard, user.uid, navigate])
  const updateEngagementAction = useCallback((id, patch) => {
    const p = dbUpdateEngagement(id, patch, user.uid)
    const current = engagements.find((e) => e.id === id)
    if (current) {
      const next = { ...current, ...patch }
      // Every edit bumps updatedAt (the staleness clock), so every edit of a
      // projected row must re-sync — else fee/stage/link edits leave the chip
      // reading "Not synced" forever. Upsert is one idempotent PATCH.
      if (next.date || next.calendarEventId) p.then(() => fireCalendarSync('engagement', next)).catch(() => {})
    }
    return p
  }, [user.uid, engagements, fireCalendarSync])
  // AboutCard hands back (key, value, isCore). Core keys are real columns;
  // anything else lands under `custom.` exactly as it does for contacts/companies.
  // Routed through updateEngagementAction so a date edit still re-syncs the
  // calendar event rather than silently going stale.
  const changeEngagementField = useCallback((key, value, isCore) => {
    const id = engRecordId
    if (!id) return undefined
    return updateEngagementAction(id, isCore ? { [key]: value } : { [`custom.${key}`]: value })
  }, [engRecordId, updateEngagementAction])
  const deleteEngagementAction = useCallback(
    async (id) => {
      const doomed = engagements.find((e) => e.id === id)
      if (doomed) removeCalendarEvent(doomed, googleCalendarId)
      await dbDeleteEngagement(id)
      navigate(route.type === 'engagements' && route.recordId === id ? { type: 'engagements', board: route.board } : route)
    },
    [engagements, googleCalendarId, route, navigate],
  )
  const moveEngagement = useCallback(
    (id, stage, destIndex) => {
      const colEngs = engagements.filter((e) => e.stage === stage).sort((a, b) => a.order - b.order)
      const order = orderForIndex(colEngs, id, destIndex)
      const p = dbMoveEngagementToStage(id, stage, order, user.uid)
      const row = engagements.find((e) => e.id === id)
      // Stage/order don't touch the event body, but the drag bumped updatedAt —
      // so the projected row's chip goes stale unless we re-stamp via upsert.
      if (row && (row.date || row.calendarEventId)) p.then(() => fireCalendarSync('engagement', row)).catch(() => {})
    },
    [engagements, user.uid, fireCalendarSync],
  )
  const createEngStage = useCallback((s) => dbCreateEngagementStage({ ...s, type: engBoard }, user.uid), [engBoard, user.uid])
  const updateEngStage = useCallback((id, patch) => dbUpdateEngagementStage(id, patch, user.uid), [user.uid])
  const deleteEngStage = useCallback((id) => dbDeleteEngagementStage(id), [])
  const addActionItem = useCallback(
    (engagement, title) => {
      const trimmed = (title || '').trim()
      if (!trimmed) return undefined
      return dbCreateTask(
        { projectId: ENGAGEMENT_BOARD_PROJECT[engagementType(engagement)], title: trimmed, engagementId: engagement.id, status: firstOpenStatusId, assigneeUid: user.uid, labelIds: [] },
        user.uid,
      )
    },
    [user.uid, firstOpenStatusId],
  )
  const unlinkTaskFromEngagement = useCallback((taskId) => dbUpdateTask(taskId, { engagementId: null }), [])

  // ── Calendar sync retry handlers (SyncChip surfaces) ──
  const syncEngagementCalendar = useCallback((id) => {
    const row = engagements.find((e) => e.id === id)
    if (row) fireCalendarSync('engagement', row)
  }, [engagements, fireCalendarSync])
  const syncTeamDateCalendar = useCallback((d) => fireCalendarSync('teamDate', d), [fireCalendarSync])

  // ── open-engagement record wiring (associations/activities on the engagement token) ──
  const openEngagementRecord = useMemo(
    () => (engRecordId ? engagements.find((e) => e.id === engRecordId) || null : null),
    [engRecordId, engagements],
  )
  // Resolve associated records for the right column (contacts + companies).
  const engRecordsByToken = useMemo(() => {
    const map = {}
    companies.forEach((c) => { map[`company:${c.id}`] = c })
    contacts.forEach((c) => { map[`contact:${c.id}`] = c })
    return map
  }, [companies, contacts])
  const engCandidatesByType = useMemo(() => ({ contact: contacts, company: companies }), [contacts, companies])
  const engSelfLabel = openEngagementRecord?.title || 'Engagement'
  const linkEngagement = useCallback(async (type, pickRecord) => {
    if (!engRecordId) return
    const otherLabel = type === 'contact'
      ? `${pickRecord.firstName || ''} ${pickRecord.lastName || ''}`.trim() || 'Contact'
      : (pickRecord.name || pickRecord.title || 'record')
    await linkRecords({
      selfToken: objToken('engagement', engRecordId), selfLabel: engSelfLabel,
      otherToken: objToken(type, pickRecord.id), otherLabel, uid: user.uid,
    })
  }, [engRecordId, engSelfLabel, user.uid])
  const unlinkEngagement = useCallback(async (type, otherToken) => {
    if (!engRecordId) return
    const other = engRecordsByToken[otherToken] || {}
    const otherLabel = type === 'contact'
      ? `${other.firstName || ''} ${other.lastName || ''}`.trim() || 'Contact'
      : (other.name || other.title || 'record')
    await unlinkRecords({
      selfToken: objToken('engagement', engRecordId), selfLabel: engSelfLabel,
      otherToken, otherLabel, uid: user.uid,
    })
  }, [engRecordId, engSelfLabel, engRecordsByToken, user.uid])
  // Same privacy contract as the contact-record logger in CrmRouter: a sent
  // email's subject + preview land on the shared timeline, the send metadata in
  // `meta`. Nothing else from the mailbox is ever stored.
  const logEngagementEmail = useCallback(({ to, cc, bcc, subject, body, gmailMessageId, threadId }) => {
    if (!engRecordId) return undefined
    return logActivity(objToken('engagement', engRecordId), {
      type: 'email',
      body: `${subject} — ${body}`.slice(0, 300),
      meta: { to, cc: cc || [], bcc: bcc || [], subject, gmailMessageId: gmailMessageId || null, threadId: threadId || null },
    }, user.uid)
  }, [engRecordId, user.uid])
  const addEngagementActivity = useCallback(({ type, body }) => {
    if (!engRecordId) return undefined
    return logActivity(objToken('engagement', engRecordId), { type, body }, user.uid)
  }, [engRecordId, user.uid])
  const openEngagementToken = useCallback(
    (t) => dispatchToken(t, {
      contact: openContact,
      company: openCompany,
      engagement: openEngagement,
    }),
    [openContact, openCompany, openEngagement],
  )
  // Close the engagement side panel. Keeps the index filters/search/view the record
  // URL carried (see openEngagement). replace, like closeTask: opening pushed the
  // entry, so closing must not push another one Back would reopen.
  const backToEngagementBoard = useCallback(() => navigate({ type: 'engagements', board: route.board, query: route.query }, { replace: true }), [navigate, route])

  const createStatusAction = useCallback((s) => dbCreateStatus(s, user.uid), [user.uid])
  const updateStatusAction = useCallback((id, patch) => dbUpdateStatus(id, patch, user.uid), [user.uid])
  const deleteStatusAction = useCallback((id) => dbDeleteStatus(id), [])

  const createLabelAction = useCallback((l) => dbCreateLabel(l, user.uid), [user.uid])
  const updateLabelAction = useCallback((id, patch) => dbUpdateLabel(id, patch), [])
  const deleteLabelAction = useCallback((id) => dbDeleteLabel(id), [])

  const openSettings = useCallback(() => navigate({ type: 'settings' }), [navigate])

  // In-app "Add from Slack" capture (demo affordance) — creates into the
  // current project, mirroring the mockup. The real Slack slash command lives
  // in functions/ and uses the flagged "intake" project seam.
  const createFromSlack = useCallback(
    (msg) => {
      dbCreateTask(
        {
          projectId: currentProjectId,
          title: msg.text,
          description: `Captured from Slack — ${msg.channel} · ${msg.author}`,
          assigneeUid: user.uid,
          status: 'todo',
          labelIds: [],
          source: 'slack',
        },
        user.uid,
      )
    },
    [currentProjectId, user.uid],
  )

  // Board drag-drop: move a task into a column, ordered last with fractional order.
  const moveTaskToStatus = useCallback(
    (taskId, status, destIndex) => {
      const colTasks = tasks.filter((t) => t.status === status).sort((a, b) => a.order - b.order)
      const order = orderForIndex(colTasks, taskId, destIndex)
      dbSetTaskStatus(taskId, status, user.uid, order, statusesById[status]?.name)
    },
    [tasks, user.uid, statusesById],
  )

  // ListView resolves the effective open section, so it hands us the target
  // id ('' to close) rather than a blind toggle.
  const setSection = useCallback((key) => setOpenSection(key), [])
  const toggleTheme = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), [])
  const clearFilters = useCallback(
    () => setQuery({ label: null, assignee: null, phase: null, incomplete: null, dueSoon: null }),
    [setQuery],
  )

  const applySavedView = useCallback((v) => {
    const f = v.filters || {}
    setQuery({
      view: v.viewType === 'list' ? null : v.viewType,
      label: f.labelFilter ?? null,
      assignee: f.assigneeFilter ?? null,
      phase: f.phaseFilter ?? null,
      incomplete: f.incompleteOnly ? '1' : null,
      dueSoon: f.dueSoonOnly ? '1' : null,
    })
  }, [setQuery])

  const saveCurrentView = useCallback(
    (name) =>
      createSavedView(
        {
          name,
          scope: 'project',
          viewType: view,
          filters: { labelFilter, assigneeFilter, phaseFilter, incompleteOnly, dueSoonOnly },
        },
        user.uid,
      ),
    [view, labelFilter, assigneeFilter, phaseFilter, incompleteOnly, dueSoonOnly, user.uid],
  )

  const deleteSavedViewAction = useCallback((id) => deleteSavedView(id), [])

  const selectedTask = useMemo(
    () => tasks.find((t) => t.id === selectedId) || myTasks.find((t) => t.id === selectedId) || null,
    [tasks, myTasks, selectedId],
  )

  const ctx = {
    currentUser: user,
    users,
    usersById,
    labels,
    labelsById,
    statuses: liveStatuses,
    statusesById,
    projects,
    currentProject,
    // actions
    openTask,
    closeTask,
    toggleDone,
    setStatus,
    setAssignee,
    setDue,
    updateTaskFields,
    toggleLabelOnTask,
    addCommentToTask,
    createTask,
    quickAddTask,
    createFromSlack,
    moveTaskToStatus,
    inviteTeammate,
    updateProjectMembers,
    deleteTaskById,
    renameProject,
    deleteProjectById,
    archiveProject,
    notifications,
    markNotificationRead,
    markAllNotificationsRead,
    openTaskInProject,
    openPeek,
    createStatus: createStatusAction,
    updateStatus: updateStatusAction,
    deleteStatus: deleteStatusAction,
    createLabel: createLabelAction,
    updateLabel: updateLabelAction,
    deleteLabel: deleteLabelAction,
    // Register item 17: TaskDetail's engagement picker needs the unfiltered list
    // rather than the board-scoped one. Any task may link to any engagement.
    engagements: navEngagements,
  }

  const onProjectRoute = route.type === 'project'
  const myOpenCount = useMemo(() => myTasks.filter((t) => !statusesById[t.status]?.done).length, [myTasks, statusesById])

  const [searchLocal, onSearchChange] = useSearchInput(search, setSearchQuery)
  const showSearchBox = route.type === 'project' || route.type === 'mytasks'
  const searchBox = showSearchBox ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', minWidth: 0 }}>
      <MagnifyingGlass size={14} color="var(--color-muted)" style={{ flexShrink: 0 }} />
      <input value={searchLocal} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search tasks" style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--color-white)', fontSize: 12, width: 150, minWidth: 0 }} />
      {searchLocal && <button onClick={() => onSearchChange('')} title="Clear" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}>✕</button>}
    </div>
  ) : null

  // One assembled context for the registry-driven render ladder. Every value here
  // already exists as a component local/handler above — nothing new is computed.
  // (Named screenCtx to avoid colliding with the TaskboardContext `ctx` above.)
  const screenCtx = {
    route, user,
    // settings
    archivedProjects, archiveProject, theme, toggleTheme,
    // home
    allTasks, statusesById, allEngagements, homeEngStages, engStages,
    contacts, companies, teamDates, usersById,
    users, setSearchQuery, companiesById, openPeek,
    // crm record
    contactDefs, companyDefs, engagements, navigate, openEngagement,
    emailTemplates, createTemplate, updateTemplate, deleteTemplate,
    createContactAction, createCompanyAction, changeContactField, changeCompanyField,
    // engagement record
    openEngagementRecord, boardStages, engagementDefs, engagementTasks, engAssocs, engActs,
    logEngagementEmail,
    changeEngagementField,
    engRecordsByToken, engCandidatesByType, updateEngagementAction, deleteEngagementAction,
    addActionItem, toggleDone, deleteTaskById, unlinkTaskFromEngagement,
    linkEngagement, unlinkEngagement, addEngagementActivity, openEngagementToken,
    backToEngagementBoard, syncEngagementCalendar,
    // engagements index
    engView, setEngView, engOwnerFilter, setEngOwnerFilter, engStageFilter, setEngStageFilter,
    engSearch, engViewFadeRef, filteredEngagements, createEngagementAction, moveEngagement,
    createEngStage, updateEngStage, deleteEngStage,
    // team / tools / playbook
    saveMyProfile, addTeamDate, syncTeamDateCalendar, updateTeamDateAction, deleteTeamDateAction,
    googleCalendarId, tools, createTool, updateTool, deleteTool, playbook, savePlaybook,
    // my tasks
    myTasks, projects, search, myOpenCount,
    // project workspace + fallbacks
    projectsLoaded, currentProject, setShowNewProject, view, viewFadeRef, tasks,
    labelFilter, assigneeFilter, phaseFilter, incompleteOnly, dueSoonOnly, openSection,
    setSection, currentProjectId, openTask, labelsById,
  }

  // Shared screen renderers for ids that funnel through one component. crm ids
  // (contacts/companies) all go through CrmRouter; advisory/speaking/pr share one
  // engagements block.
  const FormsScreen = (ctx) => {
    const r = ctx.route
    if (!r.formId) return <FormsIndex onOpenForm={(id) => ctx.navigate({ type: 'forms', formId: id })} />
    return (
      <FormPage
        formId={r.formId}
        tab={r.tab}
        onNavigateTab={(t) => ctx.navigate(t === 'submissions'
          ? { type: 'forms', formId: r.formId, tab: 'submissions' }
          : { type: 'forms', formId: r.formId })}
        onOpenSubmission={(id) => ctx.openPeek('submission:' + id)}
      />
    )
  }

  const crmScreen = (ctx) => (
    <CrmRouter
      route={ctx.route}
      contacts={ctx.contacts} companies={ctx.companies} engagements={ctx.engagements}
      contactDefs={ctx.contactDefs} companyDefs={ctx.companyDefs}
      usersById={ctx.usersById} users={ctx.users}
      onBack={() => ctx.navigate({ type: 'crm', object: ctx.route.object, query: ctx.route.query })}
      onOpenEngagement={ctx.openEngagement} onPeek={ctx.openPeek}
      onCreateContact={ctx.createContactAction} onCreateCompany={ctx.createCompanyAction}
      onChangeContactField={ctx.changeContactField} onChangeCompanyField={ctx.changeCompanyField}
      currentUser={ctx.user} emailTemplates={ctx.emailTemplates}
    />
  )

  // The board always renders; a record (#/<board>/<id>) opens over it in a right
  // side panel, like a task in a project — Back / ✕ / Escape close it.
  const engagementsScreen = (ctx) => (
    <>
      <EngagementToolbar view={ctx.engView} onSetView={ctx.setEngView} users={ctx.users}
        ownerFilter={ctx.engOwnerFilter} onSetOwnerFilter={ctx.setEngOwnerFilter}
        stages={ctx.boardStages} stage={ctx.engStageFilter} onSetStage={ctx.setEngStageFilter}
        search={ctx.engSearch} onSetSearch={ctx.setSearchQuery} />
      <div ref={ctx.engViewFadeRef} style={{ padding: '18px 0 0' }}>
        {ctx.engView === 'board' ? (
          <EngagementsView board={ctx.route.board} engagements={ctx.filteredEngagements} allEngagements={ctx.engagements} stages={ctx.boardStages} usersById={ctx.usersById}
            onOpen={ctx.openEngagement} onCreate={ctx.createEngagementAction} onMove={ctx.moveEngagement}
            onCreateStage={ctx.createEngStage} onUpdateStage={ctx.updateEngStage} onDeleteStage={ctx.deleteEngStage} />
        ) : ctx.engView === 'list' ? (
          <EngagementListView engagements={ctx.filteredEngagements} stages={ctx.boardStages} usersById={ctx.usersById} onOpen={ctx.openEngagement} />
        ) : ctx.engView === 'calendar' ? (
          <EngagementCalendarView engagements={ctx.filteredEngagements} usersById={ctx.usersById} onOpen={ctx.openEngagement} />
        ) : (
          <EngagementTableView engagements={ctx.filteredEngagements} stages={ctx.boardStages} usersById={ctx.usersById} onOpen={ctx.openEngagement} />
        )}
      </div>
      {ctx.route.recordId && ctx.openEngagementRecord && (
        <EngagementRecord
          variant="panel"
          key={ctx.openEngagementRecord.id}
          engagement={ctx.openEngagementRecord}
          defs={ctx.engagementDefs}
          stages={ctx.boardStages}
          users={ctx.users}
          usersById={ctx.usersById}
          linkedTasks={ctx.engagementTasks}
          statusesById={ctx.statusesById}
          associations={ctx.engAssocs}
          activities={ctx.engActs}
          recordsByToken={ctx.engRecordsByToken}
          candidatesByType={ctx.engCandidatesByType}
          onUpdate={ctx.updateEngagementAction}
          onChangeField={ctx.changeEngagementField}
          onDelete={ctx.deleteEngagementAction}
          onAddActionItem={ctx.addActionItem}
          onToggleTask={ctx.toggleDone}
          onDeleteTask={ctx.deleteTaskById}
          onUnlinkTask={ctx.unlinkTaskFromEngagement}
          onLink={ctx.linkEngagement}
          onUnlink={ctx.unlinkEngagement}
          onAddActivity={ctx.addEngagementActivity}
          onOpenToken={ctx.openEngagementToken}
          onBack={ctx.backToEngagementBoard}
          onSyncCalendar={ctx.syncEngagementCalendar}
          emailTemplates={ctx.emailTemplates}
          onLogEmail={ctx.logEngagementEmail}
          sender={ctx.user}
        />
      )}
    </>
  )

  const projectScreen = (ctx) => (
    !ctx.projectsLoaded ? (
      <div className="font-mono" style={{ color: 'var(--color-muted)', fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', padding: '40px 0' }}>Loading…</div>
    ) : !ctx.currentProject ? (
      <div style={{ padding: '48px 0', maxWidth: 460 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}><span className="tag-label" style={{ color: 'var(--color-gold)' }}>Get started</span></div>
        <h1 className="font-display" style={{ fontSize: 40, margin: '0 0 10px', lineHeight: 0.95, textTransform: 'uppercase', color: 'var(--color-white)' }}>No projects yet</h1>
        <p style={{ margin: '0 0 22px', fontSize: 14, color: 'var(--color-muted)', lineHeight: 1.6 }}>Create your first project to start tracking tasks. You can add teammates and assign work once it exists.</p>
        <button onClick={() => ctx.setShowNewProject(true)} className="btn btn-gold" style={{ fontSize: 13, padding: '12px 20px' }}>+ New Project</button>
      </div>
    ) : (
      <>
        <div className="eyebrow" style={{ marginBottom: 12 }}><span className="tag-label" style={{ color: 'var(--color-gold)' }}>Workspace</span></div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <h1 className="font-display" style={{ fontSize: 42, margin: '0 0 4px', letterSpacing: '.01em', lineHeight: 0.95, textTransform: 'uppercase', color: 'var(--color-white)' }}>{ctx.currentProject.name}</h1>
          <ProjectMenu project={ctx.currentProject} />
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--color-muted)' }}>{ctx.currentProject.subtitle}</p>
        <ProjectMembers project={ctx.currentProject} />
        <div ref={ctx.viewFadeRef}>
          {ctx.view === 'list' ? (
            <ListView tasks={ctx.tasks} labelFilter={ctx.labelFilter} assigneeFilter={ctx.assigneeFilter} phaseFilter={ctx.phaseFilter} incompleteOnly={ctx.incompleteOnly} dueSoonOnly={ctx.dueSoonOnly} search={ctx.search} openSection={ctx.openSection} onSetSection={ctx.setSection} currentProjectId={ctx.currentProjectId} />
          ) : ctx.view === 'board' ? (
            <BoardView tasks={ctx.tasks} labelFilter={ctx.labelFilter} assigneeFilter={ctx.assigneeFilter} incompleteOnly={ctx.incompleteOnly} dueSoonOnly={ctx.dueSoonOnly} search={ctx.search} />
          ) : ctx.view === 'calendar' ? (
            <CalendarView tasks={ctx.tasks} labelFilter={ctx.labelFilter} assigneeFilter={ctx.assigneeFilter} phaseFilter={ctx.phaseFilter} incompleteOnly={ctx.incompleteOnly} dueSoonOnly={ctx.dueSoonOnly} search={ctx.search} statusesById={ctx.statusesById} onOpenTask={ctx.openTask} />
          ) : (
            <TableView tasks={ctx.tasks} labelFilter={ctx.labelFilter} assigneeFilter={ctx.assigneeFilter} phaseFilter={ctx.phaseFilter} incompleteOnly={ctx.incompleteOnly} dueSoonOnly={ctx.dueSoonOnly} search={ctx.search} statusesById={ctx.statusesById} usersById={ctx.usersById} labelsById={ctx.labelsById} onOpenTask={ctx.openTask} />
          )}
        </div>
      </>
    )
  )

  const renderers = {
    settings: (ctx) => (
      <SettingsPage
        archivedProjects={ctx.archivedProjects}
        onRestoreProject={(id) => ctx.archiveProject(id, false)}
        theme={ctx.theme}
        onToggleTheme={ctx.toggleTheme}
        emailTemplates={ctx.emailTemplates}
        onCreateTemplate={ctx.createTemplate}
        onUpdateTemplate={ctx.updateTemplate}
        onDeleteTemplate={ctx.deleteTemplate}
      />
    ),
    home: (ctx) => (
      <HomeView
        user={ctx.user}
        tasks={ctx.allTasks}
        statusesById={ctx.statusesById}
        engagements={ctx.allEngagements}
        engagementStages={ctx.homeEngStages}
        contacts={ctx.contacts}
        companies={ctx.companies}
        teamDates={ctx.teamDates}
        usersById={ctx.usersById}
      />
    ),
    contacts: crmScreen,
    companies: crmScreen,
    forms: FormsScreen,
    advisory: engagementsScreen,
    speaking: engagementsScreen,
    pr: engagementsScreen,
    team: (ctx) => (
      <TeamView users={ctx.users} dates={ctx.teamDates} currentUid={ctx.user.uid} onSaveProfile={ctx.saveMyProfile} onAddDate={ctx.addTeamDate} onSyncDate={ctx.syncTeamDateCalendar} onUpdateDate={ctx.updateTeamDateAction} onDeleteDate={ctx.deleteTeamDateAction} googleCalendarId={ctx.googleCalendarId} />
    ),
    tools: (ctx) => (
      <ToolsView tools={ctx.tools} onCreate={ctx.createTool} onUpdate={ctx.updateTool} onDelete={ctx.deleteTool} />
    ),
    playbook: (ctx) => (
      <PlaybookView page={ctx.playbook} onSave={ctx.savePlaybook} canEdit />
    ),
    mytasks: (ctx) => (
      <>
        <div className="eyebrow" style={{ marginBottom: 12 }}><span className="tag-label" style={{ color: 'var(--color-gold)' }}>Your work</span></div>
        <h1 className="font-display" style={{ fontSize: 42, margin: '0 0 4px', letterSpacing: '.01em', lineHeight: 0.95, textTransform: 'uppercase', color: 'var(--color-white)' }}>My Tasks</h1>
        <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--color-muted)' }}>{ctx.myOpenCount} open · everything assigned to you, across projects.</p>
        <MyTasksView tasks={ctx.myTasks} projects={ctx.projects} search={ctx.search} bucket={ctx.route.query?.bucket || null} />
      </>
    ),
    project: projectScreen,
  }

  return (
    <TaskboardContext.Provider value={ctx}>
      <AppShell
        drawerOpen={sidebarOpen}
        onCloseDrawer={() => setSidebarOpen(false)}
        tabbar={
          <TabBar
            active={isActive('home', route) ? 'home' : isActive('mytasks', route) ? 'mytasks' : route.type === 'engagements' ? 'engagements' : 'none'}
            items={[
              { key: 'home', label: 'Home', onSelect: goToHome, icon: <House size={22} /> },
              { key: 'mytasks', label: 'My Tasks', onSelect: goToMyTasks, icon: <ListChecks size={22} /> },
              { key: 'engagements', label: 'Engagements', onSelect: goToSpeaking, icon: <UsersThree size={22} /> },
              { key: 'more', label: 'More', onSelect: () => setSidebarOpen(true), icon: <List size={22} /> },
            ]}
          />
        }
        sidebar={
          <Sidebar
            route={route}
            projects={activeProjects}
            myCount={myOpenCount}
            advisoryEngagements={advisoryEngagements}
            speakingEngagements={speakingEngagements}
            prEngagements={prEngagements}
            onOpenEngagement={openEngagement}
            onGoHome={goToHome}
            onGoMyTasks={goToMyTasks}
            onGoTeam={goToTeam}
            onGoTools={goToTools}
            onGoPlaybook={goToPlaybook}
            onGoSpeaking={goToSpeaking}
            onGoPR={goToPR}
            onGoContacts={goToContacts}
            onGoCompanies={goToCompanies}
            onGoAdvisory={goToAdvisory}
            onGoForms={goToForms}
            onSelectProject={goToProject}
            onNewProject={() => setShowNewProject(true)}
            onOpenSettings={openSettings}
            theme={theme}
            onToggleTheme={toggleTheme}
            onCloseMobile={() => setSidebarOpen(false)}
            updateProfile={updateProfile}
            signOut={signOut}
          />
        }
        topbar={
          <>
            <Hamburger onClick={() => setSidebarOpen(true)} title="Menu" />
            <div style={{ flex: 1 }} />
            {searchBox}
            <NotificationsBell />
            <button onClick={() => setShowNewTask(true)} className="btn btn-gold cn-hide-sm" style={{ fontSize: 12, padding: '9px 16px' }}>+ New Task</button>
          </>
        }
        fab={
          <Fab onClick={() => setShowNewTask(true)} title="New task" aria-label="New task">
            <Plus size={22} weight="bold" />
          </Fab>
        }
      >
        <div className="cn-workspace-surface" style={{ minHeight: '100%' }}>
          <div className="cn-workspace-grain" style={{ backgroundImage: "url('/assets/grain.svg')" }} />

          {/* project subheader (view tabs + filters) */}
          {onProjectRoute && currentProject && (
            <Toolbar
              view={view}
              onSetView={setView}
              labels={labels}
              users={users}
              labelFilter={labelFilter}
              assigneeFilter={assigneeFilter}
              onSetLabelFilter={setLabelFilter}
              onSetAssigneeFilter={setAssigneeFilter}
              phaseFilter={phaseFilter}
              onSetPhaseFilter={setPhaseFilter}
              incompleteOnly={incompleteOnly}
              onSetIncompleteOnly={setIncompleteOnly}
              dueSoonOnly={dueSoonOnly}
              onSetDueSoonOnly={setDueSoonOnly}
              onClearFilters={clearFilters}
              onCreateFromSlack={createFromSlack}
              onCreateLabel={createLabelAction}
              onUpdateLabel={updateLabelAction}
              onDeleteLabel={deleteLabelAction}
              savedViews={savedViews}
              onApplySavedView={applySavedView}
              onSaveCurrentView={saveCurrentView}
              onDeleteSavedView={deleteSavedViewAction}
            />
          )}

          <div className="cn-workspace-content" data-screen-label="Workspace">
            <div className="tb-content" style={{ maxWidth: (onProjectRoute && view !== 'list') || isFullBleed(route) ? 'none' : 880, margin: '0 auto', padding: '28px 30px 110px' }}>
              {(renderers[matchRoute(route).id] || renderers.project)(screenCtx)}
            </div>
          </div>
        </div>
      </AppShell>

      {selectedTask && <TaskDetail task={selectedTask} />}
      {peekToken(route) && peekToken(route).startsWith('submission:') && (
        <SubmissionPeekContainer
          key={peekToken(route)}
          token={peekToken(route)}
          onOpenToken={(t) => openPeek(t)}
          onClose={closePeek} />
      )}
      {peekToken(route) && !peekToken(route).startsWith('submission:') && (
        <RecordPeekContainer
          key={peekToken(route)}
          token={peekToken(route)}
          contactDefs={contactDefs} companyDefs={companyDefs}
          users={users} usersById={usersById} uid={user?.uid}
          onChangeContactField={changeContactField}
          onChangeCompanyField={changeCompanyField}
          onOpenContact={openContact} onOpenCompany={openCompany}
          onClose={closePeek} />
      )}
        {showNewTask && (
          <NewTaskModal
            projects={activeProjects}
            currentProjectId={currentProjectId}
            users={users}
            labels={labels}
            statuses={liveStatuses}
            onClose={() => setShowNewTask(false)}
            onCreate={createTask}
          />
        )}
        {showNewProject && <NewProjectModal onClose={() => setShowNewProject(false)} onCreate={createProject} />}
        <CommandPalette
          open={cmdkOpen}
          onClose={() => setCmdkOpen(false)}
          onNavigate={navigate}
          contacts={contacts}
          companies={companies}
          engagements={navEngagements}
          projects={activeProjects}
        />
    </TaskboardContext.Provider>
  )
}

// Watches the peeked record + its activities and dispatches the correct per-type
// update/delete/open handlers into the presentational RecordPeek. Rendered at
// Taskboard top level so it overlays index views AND full record pages.
function RecordPeekContainer({
  token, contactDefs, companyDefs, users, usersById, uid,
  onChangeContactField, onChangeCompanyField,
  onOpenContact, onOpenCompany, onClose,
}) {
  const { type, id } = parseToken(token)
  const [record, setRecord] = useState(null)
  const [acts, setActs] = useState([])
  const known = type === 'contact' || type === 'company'

  useEffect(() => {
    setRecord(null)
    if (!id || !known) return
    const w = type === 'company' ? watchCompany : watchContact
    return w(id, setRecord)
  }, [type, id, known])

  useEffect(() => {
    if (!id || !known) return
    return watchActivities(objToken(type, id), setActs)
  }, [type, id, known])

  if (!known || !record) return null

  const cfg = type === 'company'
    ? {
        chip: 'Company', defs: companyDefs, title: record.name || 'Company',
        onChangeField: (k, v, core) => onChangeCompanyField(id, k, v, core),
        del: () => deleteCompany(id, uid), view: () => onOpenCompany(id),
      }
    : {
        chip: 'Contact', defs: contactDefs,
        title: `${record.firstName || ''} ${record.lastName || ''}`.trim() || 'Contact',
        onChangeField: (k, v, core) => onChangeContactField(id, k, v, core),
        del: () => deleteContact(id, uid), view: () => onOpenContact(id),
      }

  const handleDelete = async () => {
    try { await cfg.del(); onClose() } catch (e) { console.error('peek delete failed', e) }
  }

  return (
    <RecordPeek
      objectChip={cfg.chip} title={cfg.title} defs={cfg.defs} record={record}
      activities={acts} users={users} usersById={usersById}
      onChangeField={cfg.onChangeField} onViewRecord={cfg.view}
      onDelete={handleDelete} onClose={onClose} />
  )
}

function SubmissionPeekContainer({ token, onOpenToken, onClose }) {
  const id = token.slice('submission:'.length)
  const [sub, setSub] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setSub(null); if (id) return watchSubmission(id, setSub) }, [id])
  if (!sub) return null

  const run = async (action) => {
    const msg = action === 'release'
      ? 'Release this submission into the CRM? This creates the contact and related records and notifies the team.'
      : 'Mark this submission as reviewed spam?'
    if (!window.confirm(msg)) return
    setBusy(true); setError('')
    try { await callPromote(id, action) }
    catch (e) { setError(promoteErrorMessage(e)) }
    finally { setBusy(false) }
  }
  const onDelete = async () => {
    if (!window.confirm('Permanently delete this submission? This cannot be undone.')) return
    setBusy(true); setError('')
    try { await deleteSubmission(id); onClose() }
    catch (e) { setError('Could not delete this submission.'); setBusy(false) }
  }

  return (
    <SubmissionPeek
      submission={sub}
      busy={busy}
      error={error}
      onRelease={() => run('release')}
      onKeep={() => run('dismiss')}
      onDelete={onDelete}
      onOpenToken={onOpenToken}
      onClose={onClose} />
  )
}

// Owns the per-record `watchContact`/`watchCompany`/`watchAssociations`/`watchActivities`
// subscriptions so the index views (Contacts/Companies lists) don't over-subscribe.
function CrmRouter({ route, contacts, companies, engagements, contactDefs, companyDefs, usersById, users, onBack, onOpenEngagement, onPeek, onCreateContact, onCreateCompany, onChangeContactField, onChangeCompanyField, currentUser, emailTemplates = [] }) {
  const [record, setRecord] = useState(null)
  const [assocs, setAssocs] = useState([])
  const [acts, setActs] = useState([])
  const { object, recordId } = route
  // token type for the current object ('companies' → 'company', etc.)
  const objType = object === 'companies' ? 'company' : 'contact'
  useEffect(() => {
    if (!recordId) { setRecord(null); return }
    const w = object === 'companies' ? watchCompany : watchContact
    return w(recordId, setRecord)
  }, [object, recordId])
  useEffect(() => {
    if (!recordId) return
    const token = objToken(objType, recordId)
    const u1 = watchAssociations(token, setAssocs)
    const u2 = watchActivities(token, setActs)
    return () => { u1(); u2() }
  }, [objType, recordId])

  // resolve associated records for the right column
  const companiesById = Object.fromEntries(companies.map((c) => [c.id, c]))
  const recordsByToken = {}
  companies.forEach((c) => { recordsByToken[`company:${c.id}`] = c })
  contacts.forEach((c) => { recordsByToken[`contact:${c.id}`] = c })
  ;(engagements || []).forEach((e) => { recordsByToken[`engagement:${e.id}`] = e })
  ;(engagements || []).forEach((e) => { recordsByToken[`engagement:${e.id}`] = e })

  if (!recordId) {
    if (object === 'companies') {
      const contactCounts = countBy(contacts, 'primaryCompanyId')
      const engagementCounts = countBy(engagements, 'primaryCompanyId')
      return <CompaniesView companies={companies} usersById={usersById} contactCounts={contactCounts} engagementCounts={engagementCounts} onOpen={(id) => onPeek(objToken('company', id))} onCreate={onCreateCompany} />
    }
    return <ContactsView contacts={contacts} companiesById={companiesById} usersById={usersById} onOpen={(id) => onPeek(objToken('contact', id))} onCreate={onCreateContact} />
  }
  if (!record) return <div style={{ padding: 40, color: 'var(--color-muted)' }}>Loading…</div>

  const token = objToken(objType, recordId)
  const uid = currentUser?.uid
  const selfLabel = object === 'companies'
    ? (record?.name || 'Company')
    : `${record?.firstName || ''} ${record?.lastName || ''}`.trim() || 'Contact'
  const candidatesByType = object === 'companies'
    ? { contact: contacts, engagement: engagements }
    : { company: companies, engagement: engagements }

  const onLink = async (type, pickRecord) => {
    const otherToken = objToken(type, pickRecord.id)
    const otherLabel = type === 'contact'
      ? `${pickRecord.firstName || ''} ${pickRecord.lastName || ''}`.trim() || 'Contact'
      : (pickRecord.name || pickRecord.title || 'record')
    const extraWrites = (object === 'contacts' && type === 'company' && !record.primaryCompanyId)
      ? [() => updateContact(record.id, { primaryCompanyId: pickRecord.id }, uid)]
      : []
    await linkRecords({ selfToken: token, selfLabel, otherToken, otherLabel, uid, extraWrites })
  }

  const onUnlink = async (type, otherToken) => {
    const other = recordsByToken[otherToken] || {}
    const otherLabel = type === 'contact'
      ? `${other.firstName || ''} ${other.lastName || ''}`.trim() || 'Contact'
      : (other.name || other.title || 'record')
    const extraWrites = (object === 'contacts' && type === 'company' && record.primaryCompanyId === parseToken(otherToken).id)
      ? [() => updateContact(record.id, { primaryCompanyId: null }, uid)]
      : []
    await unlinkRecords({ selfToken: token, selfLabel, otherToken, otherLabel, uid, extraWrites })
  }

  const onAddActivity = ({ type, body }) => logActivity(token, { type, body }, uid)

  // A failed cascade must not navigate away (the record may be half-cleaned);
  // log and leave the confirm card up so the user can retry.
  const onDeleteContact = async () => {
    try { await deleteContact(record.id, uid); onBack() } catch (e) { console.error('contact delete failed', e) }
  }
  const onDeleteCompany = async () => {
    try { await deleteCompany(record.id, uid); onBack() } catch (e) { console.error('company delete failed', e) }
  }

  // Sent-from-hub emails are the ONLY Gmail data that touches Firestore
  // (privacy contract): subject+preview as the visible body, send metadata in
  // meta. Rules make activities append-only with actorUid == self.
  // NB: meta.threadId deep-links the SENDER's mailbox only — rendering it as a
  // timeline link would 404 for everyone else.
  const onLogEmail = ({ to, cc, bcc, subject, body, gmailMessageId, threadId }) =>
    logActivity(token, {
      type: 'email',
      body: `${subject} — ${body}`.slice(0, 300),
      meta: { to, cc: cc || [], bcc: bcc || [], subject, gmailMessageId: gmailMessageId || null, threadId: threadId || null },
    }, uid)

  // Clicking a linked record peeks it (preserving the record you're on); engagements full-navigate.
  const openToken = (t) => dispatchToken(t, {
    contact: () => onPeek(t),
    company: () => onPeek(t),
    engagement: (id) => onOpenEngagement(id),
  })

  return object === 'companies'
    ? <CompanyRecord company={record} defs={companyDefs} activities={acts} associations={assocs} recordsByToken={recordsByToken} candidatesByType={candidatesByType} users={users} usersById={usersById} onChangeField={(k, v, core) => onChangeCompanyField(record.id, k, v, core)} onBack={onBack} onOpenToken={openToken} onLink={onLink} onUnlink={onUnlink} onAddActivity={onAddActivity} onDelete={onDeleteCompany} dependents={countCompanyDependents(record.id, contacts, engagements)} />
    : <ContactRecord contact={record} defs={contactDefs} activities={acts} associations={assocs} recordsByToken={recordsByToken} candidatesByType={candidatesByType} users={users} usersById={usersById} onChangeField={(k, v, core) => onChangeContactField(record.id, k, v, core)} onBack={onBack} onOpenToken={openToken} onLink={onLink} onUnlink={onUnlink} onAddActivity={onAddActivity} onLogEmail={onLogEmail} onDelete={onDeleteContact} emailTemplates={emailTemplates} sender={currentUser} />
}
